import { htmlOrTextToPlain } from '@/lib/utils/htmlPlainText'
import { LR_CHECK_ERROR_TYPE_LABELS } from '../constants'
import type { LrCheckErrorFields, YardiGuestInviteValidationError } from '../types'

/** Sanitize a dynamic `data-testid` segment. */
export function sanitizeDataTestIdSegment(value: string | number): string {
  return String(value).replace(/[^a-zA-Z0-9_-]/g, '_')
}

export function maskformatPhoneNumber(phoneNumberString: string): string {
  const cleaned = String(phoneNumberString).replace(/\D/g, '')
  const match = cleaned.match(/^([+]?\d{1,2}[.-\s]?)?(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    const intlCode = match[1] ? `+ ${match[1]}` : ''
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('')
  }
  return phoneNumberString
}

export function formatError(error: unknown): string {
  if (typeof error === 'string') return error
  if (error === undefined) return ''
  try {
    const s = JSON.stringify(error)
    return s === undefined ? String(error) : s
  } catch {
    return String(error)
  }
}

/** Unwrap repeatedly stringified JSON (some APIs double-encode `lr_check_error`). */
function peelStringifiedJson(err: unknown): unknown {
  let cur: unknown = err
  for (let i = 0; i < 8; i++) {
    if (typeof cur !== 'string') return cur
    const t = cur.trim()
    if (!t) return cur
    try {
      const next = JSON.parse(t) as unknown
      if (next === cur) return cur
      cur = next
    } catch {
      return cur
    }
  }
  return cur
}

/** Fallback for non-standard JSON: read `"error":"..."` by regex. */
function extractErrorFromJsonLikeString(s: string): string | undefined {
  const t = s.trim()
  if (!t.startsWith('{')) return undefined
  try {
    const o = JSON.parse(t) as Record<string, unknown>
    if (typeof o.error === 'string' && o.error.trim()) return o.error.trim()
    if (typeof o.message === 'string' && o.message.trim()) return o.message.trim()
  } catch {
    /* fall through */
  }
  const m = t.match(/"error"\s*:\s*"((?:[^"\\]|\\.)*)"/)
  if (m) {
    return m[1].replace(/\\"/g, '"').replace(/\\n/g, '\n').replace(/\\\\/g, '\\').trim()
  }
  return undefined
}

function extractTypeFromJsonLikeString(s: string): string | undefined {
  const t = s.trim()
  const m = t.match(/"type"\s*:\s*"((?:[^"\\]|\\.)*)"/)
  return m ? m[1].replace(/\\"/g, '"').trim() : undefined
}

/** True when `error_desc` is a non-empty string, array, or object. */
function hasNonEmptyErrorDesc(errorDesc: unknown): boolean {
  if (errorDesc == null) return false
  if (typeof errorDesc === 'string') return errorDesc.trim().length > 0
  if (Array.isArray(errorDesc)) return errorDesc.length > 0
  if (typeof errorDesc === 'object') return Object.keys(errorDesc as object).length > 0
  return true
}

/** Tooltip value: strings (incl. HTML) as-is; objects/arrays as pretty JSON. */
export function serializeLrCheckTooltipValue(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value)
  }
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

/** Coerce a scalar `error` / `message` field to trimmed text. */
function lrScalarText(v: unknown): string | undefined {
  if (v == null) return undefined
  if (typeof v === 'string') {
    const t = v.trim()
    return t ? t : undefined
  }
  if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint') {
    return String(v)
  }
  return undefined
}

/** Normalize `lr_check_error` whether it arrives as an object or (double-)stringified JSON. */
export function getLrCheckErrorFields(err: unknown): LrCheckErrorFields {
  if (err === undefined || err === null) return {}
  const peeled = peelStringifiedJson(err)

  if (typeof peeled === 'object' && peeled !== null && !Array.isArray(peeled)) {
    const o = peeled as Record<string, unknown>
    return {
      error: lrScalarText(o.error),
      error_desc: o.error_desc,
      type: typeof o.type === 'string' ? o.type : undefined,
      message: lrScalarText(o.message),
    }
  }

  if (typeof peeled === 'string') {
    const t = peeled.trim()
    if (!t) return {}
    if (t.startsWith('{')) {
      try {
        const o = JSON.parse(t) as Record<string, unknown>
        return {
          error: lrScalarText(o.error),
          error_desc: o.error_desc,
          type: typeof o.type === 'string' ? o.type : undefined,
          message: lrScalarText(o.message),
        }
      } catch {
        const recovered = extractErrorFromJsonLikeString(t)
        if (recovered) {
          return {
            error: recovered,
            type: extractTypeFromJsonLikeString(t),
          }
        }
        return {}
      }
    }
    return { error: t }
  }

  return {}
}

/** Plain, user-facing line for table cells — no raw JSON, no HTML tags. */
export function formatLrCheckErrorForDisplay(err: unknown): string {
  const { error, error_desc: errorDesc, type, message } = getLrCheckErrorFields(err)
  const hasDesc = hasNonEmptyErrorDesc(errorDesc)
  if (error?.trim() && hasDesc) return htmlOrTextToPlain(error)
  if (type && LR_CHECK_ERROR_TYPE_LABELS[type]) return LR_CHECK_ERROR_TYPE_LABELS[type]
  if (error?.trim()) return htmlOrTextToPlain(error)
  if (message?.trim()) return htmlOrTextToPlain(message)
  if (typeof err === 'object' && err !== null && !Array.isArray(err)) {
    const o = err as Record<string, unknown>
    const rawMsg = lrScalarText(o.message)
    if (rawMsg) return htmlOrTextToPlain(rawMsg)
  }
  return htmlOrTextToPlain(formatError(err))
}

/** Raw tooltip HTML payload. Use {@link getLrCheckErrorTooltipPlainText} for plain text. */
export function getLrCheckErrorTooltipHtml(err: unknown): string | null {
  const { error, error_desc: errorDesc, type } = getLrCheckErrorFields(err)
  if (error != null && error !== undefined && hasNonEmptyErrorDesc(errorDesc)) {
    const detailStr = serializeLrCheckTooltipValue(errorDesc)
    const headlineStr =
      typeof error === 'string' ? error : serializeLrCheckTooltipValue(error)
    if (detailStr === headlineStr) return null
    return detailStr
  }
  if (type && error != null && LR_CHECK_ERROR_TYPE_LABELS[type]) {
    return typeof error === 'string' ? error : serializeLrCheckTooltipValue(error)
  }
  return null
}

/** Plain tooltip text (HTML stripped) derived from {@link getLrCheckErrorTooltipHtml}. */
export function getLrCheckErrorTooltipPlainText(err: unknown): string | null {
  const html = getLrCheckErrorTooltipHtml(err)
  if (!html) return null
  const plain = htmlOrTextToPlain(html)
  return plain.trim() ? plain : null
}

export function isErrorString(error: unknown): boolean {
  return typeof error === 'string'
}

function isValidationWarning(err: YardiGuestInviteValidationError): boolean {
  return err.severity === 'warning'
}

/** Column header for validation issues: Error, Warning, or Error / Warning. */
export function getValidationColumnHeaderLabel(
  errors: YardiGuestInviteValidationError[],
  fallback = 'Error / Warning',
): string {
  const hasErrors = errors.some((err) => !isValidationWarning(err))
  const hasWarnings = errors.some(isValidationWarning)
  if (hasErrors && hasWarnings) return 'Error / Warning'
  if (hasErrors) return 'Error'
  if (hasWarnings) return 'Warning'
  return fallback
}

const VALIDATION_ERROR_TITLE_OVERRIDES: Record<string, string> = {
  INVALID_DOB: 'Invalid DOB',
}

export function formatValidationErrorTitle(type: string): string {
  const normalized = type.trim().toUpperCase()
  if (VALIDATION_ERROR_TITLE_OVERRIDES[normalized]) {
    return VALIDATION_ERROR_TITLE_OVERRIDES[normalized]
  }
  return type
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

export type ValidationMessageSegment = { text: string; bold?: boolean }

/** Title-case each word for display (Yardi often returns lowercase names). */
export function formatDisplayName(value: string): string {
  return String(value ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

const APPLICANT_LABEL_PATTERN =
  /(Applicant|Co-applicant)\s+(.+?)\s+with prospect id\s+(\S+)/i

/** Split validation copy so applicant name and prospect id can be emphasized in tooltips. */
export function parseValidationErrorMessage(message: string): ValidationMessageSegment[] {
  const match = message.match(APPLICANT_LABEL_PATTERN)
  if (!match) return [{ text: message }]

  const [full, role, name, prospectId] = match
  const start = message.indexOf(full)
  const before = message.slice(0, start)
  const after = message.slice(start + full.length)
  const segments: ValidationMessageSegment[] = []

  if (before) segments.push({ text: before })
  segments.push({ text: `${role} ` })
  segments.push({ text: formatDisplayName(name), bold: true })
  segments.push({ text: ' with prospect id ' })
  segments.push({ text: prospectId, bold: true })
  if (after) segments.push({ text: after })
  return segments
}

const CONTACT_REQUIRED_ERROR_TYPES = new Set([
  'EMAIL_OR_MOBILE_REQUIRED',
  'COAPPLICANT_EMAIL_OR_MOBILE_REQUIRED',
])

function hasValidationFieldError(
  errors: YardiGuestInviteValidationError[],
  ...fields: string[]
): boolean {
  const fieldSet = new Set(fields)
  return errors.some((err) => fieldSet.has(err.field) && !isValidationWarning(err))
}

function hasValidationFieldWarning(
  errors: YardiGuestInviteValidationError[],
  ...fields: string[]
): boolean {
  const fieldSet = new Set(fields)
  return errors.some((err) => fieldSet.has(err.field) && isValidationWarning(err))
}

export function hasContactRequiredError(errors: YardiGuestInviteValidationError[]): boolean {
  return errors.some(
    (err) =>
      !isValidationWarning(err) &&
      CONTACT_REQUIRED_ERROR_TYPES.has(err.type) &&
      err.field === 'email',
  )
}

export function getEmailValidationState(
  errors: YardiGuestInviteValidationError[],
  isEmpty: boolean,
): { hasError: boolean; hasWarning: boolean } {
  const contactRequired = hasContactRequiredError(errors)
  const hasError = hasValidationFieldError(errors, 'email') || (contactRequired && isEmpty)
  const hasWarning = hasValidationFieldWarning(errors, 'email')
  return {
    hasError,
    hasWarning: hasWarning && !hasError,
  }
}

export function getPhoneValidationState(
  errors: YardiGuestInviteValidationError[],
  isEmpty: boolean,
): { hasError: boolean; hasWarning: boolean } {
  const contactRequired = hasContactRequiredError(errors)
  const hasError =
    hasValidationFieldError(errors, 'mobileNo', 'mobile_no') || (contactRequired && isEmpty)
  const hasWarning = hasValidationFieldWarning(errors, 'mobileNo', 'mobile_no')
  return {
    hasError,
    hasWarning: hasWarning && !hasError,
  }
}

export { getTooltipPositionClass } from '@/lib/utils/tooltipPosition'
